/**
 * Administration contracts (portal): organization structure, accounts, enrollments, join requests,
 * audit log. Authorization is decided by the server and the database, never by these shapes.
 */
import { z } from "zod";
import { CircleSchedule } from "./availability.js";
import { Gender, password, phone } from "./fields.js";
const name = z.string().trim().min(2, "الاسم قصير").max(120);
const optionalText = (max) => z.string().trim().max(max).optional();
const ROLE_VALUES = ["org_admin", "branch_manager", "supervisor", "teacher", "student"];
const GrantableRole = z.enum(ROLE_VALUES);
// ---------- organization ----------
/** Live session timeouts (docs/06), in seconds; the server keeps each within its allowed range. */
export const LiveTimeoutsSetting = z.object({
    /** How long a request waits for a teacher: 1 minute to 2 hours. */
    acceptSeconds: z.number().int().min(60).max(7200),
    /** From acceptance until both have audio: 15 seconds to 5 minutes. */
    connectSeconds: z.number().int().min(15).max(300),
    /** Grace after a drop before the session is interrupted: 30 seconds to 10 minutes. */
    reconnectSeconds: z.number().int().min(30).max(600),
});
/** Attendance (B14, 2026-10-18): the hour of the next morning at which a study day closes, 0–6. */
export const AttendanceSetting = z.object({ dayCutoffHour: z.number().int().min(0).max(6) });
/**
 * Student sign-up (owner decision 2026-11-03): the cumulative review's daily amount is this many times the
 * daily memorization (3: a page memorized, three reviewed).
 */
export const RegistrationSetting = z.object({ reviewMultiple: z.number().min(1).max(10) });
export const OrgSettings = z.object({
    /** A5: gendered circles accept only teachers and students of the same gender. Default on. */
    genderSeparation: z.boolean(),
    liveTimeouts: LiveTimeoutsSetting,
    attendance: AttendanceSetting,
    registration: RegistrationSetting,
});
export const Organization = z.object({ id: z.uuid(), name: z.string(), settings: OrgSettings });
export const OrganizationsResponse = z.object({ organizations: z.array(Organization) });
export const UpdateOrgSettingsBody = OrgSettings.partial();
/** Organization holidays: no attendance is expected and plans do not advance (owner decision 2026-10-18). */
export const Holiday = z.object({ id: z.uuid(), date: z.string(), name: z.string() });
export const HolidaysResponse = z.object({ holidays: z.array(Holiday) });
export const HolidaysQuery = z.object({ organizationId: z.uuid() });
export const CreateHolidayBody = z.object({
    organizationId: z.uuid(),
    date: z.iso.date(),
    name: z.string().trim().min(2, "اكتب اسم الإجازة").max(80),
});
/** Plans moved around the new holiday (their days on it are generated again after it). */
export const CreateHolidayResponse = z.object({ holiday: Holiday, plansMoved: z.number() });
export const OverviewResponse = z.object({
    branches: z.number(),
    circles: z.number(),
    students: z.number(),
    teachers: z.number(),
    pendingJoinRequests: z.number(),
    /** Organization administration only; 0 for everyone else. */
    pendingDeletionRequests: z.number(),
});
// ---------- branches ----------
export const Branch = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    name: z.string(),
    code: z.string(),
    city: z.string().nullable(),
    acceptsJoinRequests: z.boolean(),
    archivedAt: z.string().nullable(),
    circleCount: z.number(),
    studentCount: z.number(),
});
export const BranchesResponse = z.object({ branches: z.array(Branch) });
const code = z
    .string()
    .trim()
    .min(1)
    .max(16)
    .regex(/^[A-Za-z0-9-]+$/, "رمز الفرع: أحرف إنجليزية وأرقام و- فقط");
export const CreateBranchBody = z.object({
    organizationId: z.uuid(),
    name,
    code,
    city: optionalText(80),
});
export const UpdateBranchBody = z.object({
    name: name.optional(),
    code: code.optional(),
    city: z.string().trim().max(80).nullable().optional(),
    acceptsJoinRequests: z.boolean().optional(),
    archived: z.boolean().optional(),
});
// ---------- programs (B1) ----------
export const PROGRAM_KINDS = [
    "memorization",
    "review",
    "memorization_review",
    "ijazah",
    "taahud",
    "other",
];
export const ProgramKind = z.enum(PROGRAM_KINDS);
export const Program = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    name: z.string(),
    kind: ProgramKind,
    archivedAt: z.string().nullable(),
});
export const ProgramsResponse = z.object({ programs: z.array(Program) });
export const CreateProgramBody = z.object({ organizationId: z.uuid(), name, kind: ProgramKind });
export const UpdateProgramBody = z.object({
    name: name.optional(),
    kind: ProgramKind.optional(),
    archived: z.boolean().optional(),
});
// ---------- circles ----------
const Person = z.object({ id: z.uuid(), name: z.string() });
export const CircleSummary = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    branchId: z.uuid(),
    branchName: z.string(),
    programId: z.uuid().nullable(),
    programName: z.string().nullable(),
    name: z.string(),
    gender: Gender.nullable(),
    capacity: z.number().nullable(),
    /** When the circle studies each day ("HH:MM", organization time); drives the daily wird reminder. */
    studyTime: z.string().nullable(),
    /** When students request live recitation: fixed periods, or open at any time. */
    schedule: CircleSchedule,
    archivedAt: z.string().nullable(),
    teachers: z.array(Person),
    studentCount: z.number(),
});
export const CircleListResponse = z.object({ circles: z.array(CircleSummary) });
const capacity = z.number().int().min(1).max(500).nullable();
const studyTime = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "اكتب الوقت بصيغة 16:30")
    .nullable();
export const CreateCircleBody = z.object({
    branchId: z.uuid(),
    name,
    gender: Gender.nullable(),
    capacity: capacity.optional(),
    programId: z.uuid().nullable().optional(),
    studyTime: studyTime.optional(),
    schedule: CircleSchedule.optional(),
});
export const UpdateCircleBody = z.object({
    name: name.optional(),
    gender: Gender.nullable().optional(),
    capacity: capacity.optional(),
    programId: z.uuid().nullable().optional(),
    studyTime: studyTime.optional(),
    schedule: CircleSchedule.optional(),
    archived: z.boolean().optional(),
});
export const AssignTeacherBody = z.object({ teacherId: z.uuid() });
// ---------- users ----------
export const UserStatus = z.enum(["active", "suspended", "archived"]);
export const RoleGrant = z.object({
    assignmentId: z.uuid(),
    role: GrantableRole,
    scopeType: z.enum(["organization", "branch", "circle", "self"]),
    scopeId: z.uuid().nullable(),
    /** Human name of the scope (organization, branch or circle), when visible to the caller. */
    scopeName: z.string().nullable(),
    /** The grant's role profile (ADR 0018); null: the role's default permissions. */
    profileId: z.uuid().nullable(),
    profileName: z.string().nullable(),
});
export const EnrollmentSummary = z.object({
    id: z.uuid(),
    circleId: z.uuid(),
    circleName: z.string(),
    branchId: z.uuid(),
    branchName: z.string(),
    status: z.enum(["active", "paused", "transferred", "completed", "withdrawn"]),
    startedOn: z.string(),
    endedOn: z.string().nullable(),
});
export const UserSummary = z.object({
    id: z.uuid(),
    name: z.string(),
    phone: z.string().nullable(),
    gender: Gender.nullable(),
    status: UserStatus,
    createdAt: z.string(),
    roles: z.array(RoleGrant),
    enrollments: z.array(EnrollmentSummary),
    /** Hint for the UI; the server checks again on every change. */
    manageable: z.boolean(),
});
export const UsersQuery = z.object({
    role: GrantableRole.optional(),
    branchId: z.uuid().optional(),
    circleId: z.uuid().optional(),
    status: UserStatus.optional(),
    q: z.string().trim().max(64).optional(),
});
export const UsersResponse = z.object({ users: z.array(UserSummary) });
export const UserResponse = z.object({ user: UserSummary });
export const UpdateUserBody = z.object({
    name: name.optional(),
    phone: phone.nullable().optional(),
    gender: Gender.nullable().optional(),
    status: z.enum(["active", "suspended"]).optional(),
});
export const ResetPasswordBody = z.object({ password });
export const GrantRoleBody = z.object({
    organizationId: z.uuid(),
    role: GrantableRole,
    scopeType: z.enum(["organization", "branch", "circle", "self"]),
    scopeId: z.uuid().nullable(),
    /** Staff roles: the role profile (ADR 0018); omitted, the role's default permissions. */
    profileId: z.uuid().nullable().optional(),
});
export const RevokeRoleBody = z.object({ reason: optionalText(300) });
/** Changes a grant in one step (role, scope or profile): the old grant is revoked and a new one made. */
export const UpdateAssignmentBody = z.object({
    role: GrantableRole,
    scopeType: z.enum(["organization", "branch", "circle", "self"]),
    scopeId: z.uuid().nullable(),
    profileId: z.uuid().nullable().optional(),
});
// ---------- enrollments ----------
export const CreateEnrollmentBody = z.object({ studentId: z.uuid(), circleId: z.uuid() });
export const TransferEnrollmentBody = z.object({ circleId: z.uuid(), reason: optionalText(300) });
export const EndEnrollmentBody = z.object({
    status: z.enum(["withdrawn", "completed"]),
    reason: optionalText(300),
});
export const EnrollmentResponse = z.object({ enrollmentId: z.uuid() });
// ---------- join requests (A6) ----------
export const PublicBranch = z.object({
    id: z.uuid(),
    name: z.string(),
    city: z.string().nullable(),
    organizationName: z.string(),
});
export const PublicBranchesResponse = z.object({ branches: z.array(PublicBranch) });
/** From the app (owner decision 2026-10-21): no branch; the administration chooses it on acceptance. */
export const SubmitJoinRequestBody = z.object({
    organizationId: z.uuid(),
    name,
    gender: Gender,
    phone,
    guardianPhone: phone.optional(),
    birthYear: z.number().int().min(1930).max(2030).optional(),
    note: optionalText(500),
    /** Honeypot: humans never fill it. */
    website: z.string().max(200).optional(),
});
export const JoinRequestStatus = z.enum(["pending", "accepted", "rejected"]);
export const JoinRequest = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    /** Null for requests from the app: the branch is chosen on acceptance. */
    branchId: z.uuid().nullable(),
    branchName: z.string().nullable(),
    name: z.string(),
    gender: Gender,
    phone: z.string(),
    guardianPhone: z.string().nullable(),
    birthYear: z.number().nullable(),
    note: z.string().nullable(),
    status: JoinRequestStatus,
    createdAt: z.string(),
    decidedAt: z.string().nullable(),
    decisionNote: z.string().nullable(),
    userId: z.uuid().nullable(),
});
export const JoinRequestsResponse = z.object({ joinRequests: z.array(JoinRequest) });
export const RejectJoinRequestBody = z.object({ note: optionalText(300) });
// ---------- audit log ----------
export const AuditEntry = z.object({
    id: z.number(),
    at: z.string(),
    actor: Person.nullable(),
    actorRole: z.string().nullable(),
    action: z.string(),
    entityType: z.string(),
    entityId: z.string().nullable(),
    before: z.unknown(),
    after: z.unknown(),
});
export const AuditQuery = z.object({
    organizationId: z.uuid(),
    /** Cursor: return entries older than this id. */
    before: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const AuditResponse = z.object({ entries: z.array(AuditEntry), nextBefore: z.number().nullable() });
//# sourceMappingURL=admin.js.map